/**
 * Session-scoped "the operator deliberately signed out" flag.
 *
 * Distinguishes "an operator explicitly signed out in this browsing session"
 * from "a kiosk player booted signed out". Both look identical to the router
 * (no Clerk session + a stored screen credential), but they must route
 * differently: a kiosk boot belongs on `/player`, while an operator who just
 * signed out must reach `/sign-in` instead of being bounced back into the
 * kiosk by KioskRouteGuard / HomeRedirect.
 *
 * The flag lives in sessionStorage so it dies with the tab — a real kiosk
 * reboot always starts clean. The durable screen credential is deliberately
 * left untouched: signing out of the control app must never sign out the
 * device. The flag is cleared wherever the player surface is deliberately
 * entered (credential adoption, PlayerRuntime mount) so a later intentional
 * kiosk boot re-arms the guard.
 */

const OPERATOR_EXITED_KEY = "omnimedia.operatorExited";

function storage(): Storage | null {
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    // Storage access can throw (e.g. sandboxed iframes) — treat as absent.
    return null;
  }
}

/** Record that the operator explicitly signed out in this browsing session. */
export function markOperatorExited(): void {
  try {
    storage()?.setItem(OPERATOR_EXITED_KEY, "1");
  } catch {
    // Best-effort — quota/security errors just mean the guard stays armed.
  }
}

/** Re-arm the kiosk guard — called when the player surface is deliberately entered. */
export function clearOperatorExited(): void {
  try {
    storage()?.removeItem(OPERATOR_EXITED_KEY);
  } catch {
    // Best-effort.
  }
}

/** True when the operator deliberately signed out in this browsing session. */
export function hasOperatorExited(): boolean {
  try {
    return storage()?.getItem(OPERATOR_EXITED_KEY) === "1";
  } catch {
    return false;
  }
}
